import { DateTime } from "luxon";
import React, { useState } from "react";
import Control from "../../../common/Controls/Control";
import { filterContainerClassName } from "../../../common/Controls/filterContainer";
import tools from "../../../../helpers/tools";
import { PJ_SEARCH_TYPES } from "../atoms/pjRenewListAtoms";
import usePjRenewList from "../usePjRenewList";

const PERIOD_PRESETS = [
  { nbDays: 90, name: "90 derniers jours" },
  { nbDays: 180, name: "180 derniers jours" },
  { nbDays: 365, name: "1 an" },
];

const getPresetPeriod = (nbDays) => ({
  dateDebut: DateTime.local().minus({ days: nbDays }).startOf("day").toISO(),
  dateFin: DateTime.local().endOf("day").toISO(),
});

const PjRenewListFilters = () => {
  const {
    actions: { changeSearch },
    pjListFilters,
  } = usePjRenewList();

  const { searchType, filters } = pjListFilters;
  const { renewableInNbDays } = filters;

  const [nbDays, setNbDays] = useState(
    renewableInNbDays > 0 ? renewableInNbDays : "30"
  );
  const [periodPreset, setPeriodPreset] = useState(PERIOD_PRESETS[0].nbDays);
  const [customPeriod, setCustomPeriod] = useState({
    dateDebut: "",
    dateFin: "",
  });
  const [terminatedFilters, setTerminatedFilters] = useState({
    societe: "",
    email: "",
  });

  const isARenouveler = searchType == PJ_SEARCH_TYPES.A_RENOUVELER;
  const isEffectues = searchType == PJ_SEARCH_TYPES.EFFECTUES;
  const isResilies = searchType == PJ_SEARCH_TYPES.RESILIES;
  const isDateInFutur = isARenouveler && renewableInNbDays > 0;
  const isPastDue = isARenouveler && !renewableInNbDays;

  const handlePastDueClick = () => {
    changeSearch({
      searchType: PJ_SEARCH_TYPES.A_RENOUVELER,
      filters: { renewableInNbDays: null },
    });
  };

  const handleRenewableClick = () => {
    changeSearch({
      searchType: PJ_SEARCH_TYPES.A_RENOUVELER,
      filters: { renewableInNbDays: nbDays },
    });
  };

  const handleChangeNbDays = (e) => {
    const { value } = e.target;
    if (!value) return;
    setNbDays(value);
    changeSearch({
      searchType: PJ_SEARCH_TYPES.A_RENOUVELER,
      filters: { renewableInNbDays: value },
    });
  };

  const applyPreset = (presetNbDays) => {
    setPeriodPreset(presetNbDays);
    changeSearch({
      searchType: PJ_SEARCH_TYPES.EFFECTUES,
      filters: getPresetPeriod(presetNbDays),
    });
  };

  const searchCustomPeriod = (period) => {
    changeSearch({
      searchType: PJ_SEARCH_TYPES.EFFECTUES,
      filters: {
        dateDebut: DateTime.fromISO(period.dateDebut).startOf("day").toISO(),
        dateFin: DateTime.fromISO(period.dateFin).endOf("day").toISO(),
      },
    });
  };

  const handleCustomPeriodClick = () => {
    setPeriodPreset("custom");
    if (customPeriod.dateDebut && customPeriod.dateFin) {
      searchCustomPeriod(customPeriod);
    }
  };

  const handleChangeCustomPeriod = (e) => {
    const { name, value } = e.target;
    const newPeriod = { ...customPeriod, [name]: value };
    setCustomPeriod(newPeriod);
    if (newPeriod.dateDebut && newPeriod.dateFin) {
      searchCustomPeriod(newPeriod);
    }
  };

  const searchTerminated = () => {
    // Joi côté API refuse la chaîne vide sur societe/email : on omet les champs vides
    const filters = {};
    if (terminatedFilters.societe) filters.societe = terminatedFilters.societe;
    if (terminatedFilters.email) filters.email = terminatedFilters.email;
    changeSearch({
      searchType: PJ_SEARCH_TYPES.RESILIES,
      filters,
    });
  };

  const handleChangeTerminatedFilters = (e) => {
    const { name, value } = e.target;
    setTerminatedFilters({ ...terminatedFilters, [name]: value });
  };

  const handleTerminatedSubmit = (e) => {
    e.preventDefault();
    searchTerminated();
  };

  return (
    <div className={filterContainerClassName}>
      <div
        className="col-12 d-flex flex-wrap align-items-center"
        style={{ gap: 8 }}
      >
        <button
          onClick={handlePastDueClick}
          className={`btn btn-${isPastDue ? "primary" : "default"}`}
        >
          Échéance dépassée, non renouvelées
        </button>
        <button
          onClick={handleRenewableClick}
          className={`btn btn-${isDateInFutur ? "primary" : "default"}`}
        >
          Échéance dans moins de {nbDays} jours
        </button>
        <button
          onClick={() => applyPreset(periodPreset == "custom" ? 90 : periodPreset)}
          className={`btn btn-${isEffectues ? "primary" : "default"}`}
        >
          Renouvellements effectués
        </button>
        <button
          onClick={searchTerminated}
          className={`btn btn-${isResilies ? "primary" : "default"}`}
        >
          Contrats résiliés
        </button>
      </div>

      {isDateInFutur && (
        <div className="col-12 col-md-4">
          <Control
            label="Ajuster la durée"
            type="select"
            value={nbDays}
            change={handleChangeNbDays}
            datas={Array(3)
              .fill(1)
              .map((i, ik) => ({
                id: (ik + 1) * 30,
                name: `${(ik + 1) * 30} jours`,
              }))}
            dataIndex="id"
            dataLabel="name"
            noPaddingOnFormGroup
            noMarginOnFormGroup
            inputStyle={{ borderRadius: 8 }}
            labelClassname="mt-2"
            noBg
          />
        </div>
      )}

      {isEffectues && (
        <div
          className="col-12 d-flex flex-wrap align-items-end mt-3"
          style={{ gap: 8 }}
        >
          {PERIOD_PRESETS.map((preset) => (
            <button
              key={`preset${preset.nbDays}`}
              onClick={() => applyPreset(preset.nbDays)}
              className={`btn btn-${
                periodPreset == preset.nbDays ? "primary" : "default"
              }`}
            >
              {preset.name}
            </button>
          ))}
          <button
            onClick={handleCustomPeriodClick}
            className={`btn btn-${
              periodPreset == "custom" ? "primary" : "default"
            }`}
          >
            Période personnalisée
          </button>
          {periodPreset == "custom" && (
            <>
              <Control
                label="Entre le"
                type="date"
                name="dateDebut"
                value={tools.getDateFrom(customPeriod.dateDebut)}
                change={handleChangeCustomPeriod}
                labelStyle={{ marginBottom: 0 }}
                noPaddingOnFormGroup
                noMarginOnFormGroup
                noBg
              />
              <Control
                label="et le"
                type="date"
                name="dateFin"
                value={tools.getDateFrom(customPeriod.dateFin)}
                change={handleChangeCustomPeriod}
                labelStyle={{ marginBottom: 0 }}
                noPaddingOnFormGroup
                noMarginOnFormGroup
                noBg
              />
            </>
          )}
        </div>
      )}

      {isResilies && (
        <form
          className="col-12 d-flex flex-wrap align-items-end mt-3"
          style={{ gap: 8 }}
          onSubmit={handleTerminatedSubmit}
        >
          <Control
            label="Nom de société"
            type="text"
            name="societe"
            value={terminatedFilters.societe}
            change={handleChangeTerminatedFilters}
            labelStyle={{ marginBottom: 0 }}
            noPaddingOnFormGroup
            noMarginOnFormGroup
            noBg
          />
          <Control
            label="Email du client"
            type="text"
            name="email"
            value={terminatedFilters.email}
            change={handleChangeTerminatedFilters}
            labelStyle={{ marginBottom: 0 }}
            noPaddingOnFormGroup
            noMarginOnFormGroup
            noBg
          />
          <button type="submit" className="btn btn-primary">
            Rechercher
          </button>
        </form>
      )}
    </div>
  );
};

export default PjRenewListFilters;
